<template>
  <b-card>
    <b-alert
      :show="Boolean(error)"
      variant="danger"
    >
      {{ error }}
    </b-alert>
    <b-form @submit.prevent="submit">
      <b-form-group
        :label="words.title"
        label-for="menu-title"
      >
        <b-form-input
          id="menu-title"
          v-model="form.title"
          required
          :disabled="busy"
        />
      </b-form-group>
      <b-form-group
        label="Slug"
        label-for="menu-slug"
      >
        <b-form-input
          id="menu-slug"
          v-model="form.slug"
          required
          :disabled="busy"
        />
      </b-form-group>
      <b-form-group
        :label="words.description"
        label-for="menu-description"
      >
        <b-form-textarea
          id="menu-description"
          v-model="form.description"
          :disabled="busy"
        />
      </b-form-group>
      <b-form-group
        :label="words.language"
        label-for="menu-lang"
      >
        <b-form-select
          id="menu-lang"
          v-model="lang"
          :options="['fa', 'en', 'ar']"
          :disabled="busy"
        />
      </b-form-group>
      <b-form-group
        :label="words.linkType"
        label-for="menu-type"
      >
        <b-form-select
          id="menu-type"
          v-model="form.linkType"
          :options="[{ value: 'InternalLink', text: words.internal }, { value: 'ExternalLink', text: words.external }]"
          :disabled="busy"
        />
      </b-form-group>
      <b-form-group
        :label="words.url"
        label-for="menu-url"
      >
        <b-form-input
          id="menu-url"
          v-model="form.Link"
          :disabled="busy"
        />
      </b-form-group>
      <b-button
        type="submit"
        variant="primary"
        :disabled="busy || !allowed || !ready"
      >
        {{ words.save }}
      </b-button>
      <b-button
        class="ml-1"
        variant="outline-secondary"
        @click="$router.go(-1)"
      >
        {{ words.back }}
      </b-button>
    </b-form>
  </b-card>
</template>
<script>
import {
  BAlert, BButton, BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea,
} from 'bootstrap-vue'
import axios from '@axios'
import menuPayload from './menu-payload'
import messages from './menu-messages'

export default {
  name: 'MenuForm',
  components: {
    BAlert, BButton, BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea,
  },
  props: { id: { type: String, default: '' } },
  data() {
    return {
      form: {
        title: '', slug: '', description: '', linkType: 'InternalLink', Link: '',
      },
      lang: 'fa',
      loading: false,
      saving: false,
      requestSequence: 0,
      disposed: false,
      loadedSnapshot: null,
      error: '',
    }
  },
  computed: {
    busy() { return this.loading || this.saving },
    ready() { return Boolean(this.loadedSnapshot && this.loadedSnapshot.id === this.id && this.loadedSnapshot.lang === this.lang) },
    words() { return messages[this.$i18n.locale] || messages.fa },
    allowed() { return this.$can(this.id ? 'update' : 'create', 'menu') },
  },
  watch: {
    id() { this.load() },
    lang() { this.load() },
  },
  created() { this.load() },
  beforeDestroy() { this.disposed = true; this.requestSequence += 1 },
  methods: {
    async load() {
      const sequence = this.requestSequence + 1
      this.requestSequence = sequence
      const { id } = this
      const { lang } = this
      this.loadedSnapshot = null
      this.form = {
        title: '', slug: '', description: '', linkType: 'InternalLink', Link: '',
      }
      this.loading = Boolean(id)
      this.error = ''
      if (!id) {
        this.loadedSnapshot = Object.freeze({ id, lang, fields: Object.freeze({ ...this.form }) })
        return
      }
      try {
        const { data } = await axios.get(`/menus/${encodeURIComponent(id)}`, { headers: { lang } })
        if (this.disposed || sequence !== this.requestSequence || id !== this.id || lang !== this.lang) return
        if (!data || typeof data !== 'object' || Array.isArray(data) || (data._id && data._id !== id)) throw new Error('Invalid menu response')
        const fields = menuPayload(data, lang)
        this.loadedSnapshot = Object.freeze({ id, lang, fields: Object.freeze({ ...fields }) })
        this.form = { ...fields }
      } catch (error) {
        if (!this.disposed && sequence === this.requestSequence && id === this.id && lang === this.lang) this.error = this.words.failed
      } finally {
        if (sequence === this.requestSequence) this.loading = false
      }
    },
    async submit() {
      if (this.busy || !this.ready || !this.$can(this.id ? 'update' : 'create', 'menu') || typeof this.form.title !== 'string' || !this.form.title.trim() || typeof this.form.slug !== 'string' || !this.form.slug.trim()) return
      const link = this.form.Link || ''
      const internal = /^(?:\/(?!\/)|#)[^\s]*$/.test(link)
      const external = /^https?:\/\/[^\s]+$/i.test(link)
      const hasUnsafeCharacters = typeof link !== 'string' || Array.from(link).some(character => character === String.fromCharCode(92) || character.charCodeAt(0) < 32 || (character.charCodeAt(0) >= 127 && character.charCodeAt(0) <= 159))
      if (hasUnsafeCharacters || (link && !(this.form.linkType === 'ExternalLink' ? external : internal))) {
        this.error = this.words.invalid
        return
      }
      this.saving = true
      this.error = ''
      const { id } = this.loadedSnapshot
      const { lang } = this.loadedSnapshot
      const sequence = this.requestSequence
      const payload = menuPayload(this.form, lang)
      try {
        const config = { headers: { lang } }
        if (id) await axios.patch(`/menus/${encodeURIComponent(id)}`, payload, config)
        else await axios.post('/menus', payload, config)
        if (!this.disposed && sequence === this.requestSequence && id === this.id && lang === this.lang) this.$router.push({ name: 'pages-menus-list' })
      } catch (error) {
        if (!this.disposed && sequence === this.requestSequence && id === this.id && lang === this.lang) this.error = this.words.failed
      } finally { this.saving = false }
    },
  },
}
</script>
